import { Injectable } from '@angular/core';
import { getTimezoneOffset } from 'date-fns-tz';

export function getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** UTC offset in minutes, matching moment.tz(zone).utcOffset() semantics */
export function getTimezoneUtcOffsetMinutes(timeZone: string, date: Date = new Date()): number {
    return getTimezoneOffset(timeZone, date) / 60000;
}

@Injectable({
    providedIn: 'root'
})

export class DateUtilsService {
    /**
     * Converts Angular date format tokens to ngx-bootstrap v20 format tokens
     * Used for ngx-bootstrap datepicker configuration
     * 
     * @param angularFormat - Angular date format string (e.g., 'dd.MM.yyyy', 'MM/dd/yyyy')
     * @returns ngx-bootstrap v20 format string (e.g., 'DD.MM.YYYY', 'MM/DD/YYYY')
     */
    convertToMomentFormat(angularFormat: string): string {
        if (!angularFormat) {
            return 'MM/DD/YYYY'; // Default fallback
        }

        return angularFormat
            .replace(/dd/g, 'DD')  // Day of month (01-31)
            .replace(/MM/g, 'MM')  // Month (01-12) - already correct
            .replace(/yyyy/g, 'YYYY')  // Full year (2024)
            .replace(/yy/g, 'YY')  // Year (24)
            .replace(/d/g, 'D')  // Day of month (1-31, no leading zero)
            .replace(/M/g, 'M')  // Month (1-12, no leading zero)
            .replace(/y/g, 'Y');  // Year (24, no leading zero)
    }

    /**
     * Converts moment.js format tokens to Angular date format tokens
     * Used for reverse conversion if needed
     * 
     * @param momentFormat - moment.js format string (e.g., 'DD.MM.YYYY', 'MM/DD/YYYY')
     * @returns Angular format string (e.g., 'dd.MM.yyyy', 'MM/dd/yyyy')
     */
    convertToAngularFormat(momentFormat: string): string {
        if (!momentFormat) {
            return 'MM/dd/yyyy'; // Default fallback
        }

        return momentFormat
            .replace(/DD/g, 'dd')  // Day of month (01-31)
            .replace(/MM/g, 'MM')  // Month (01-12) - already correct
            .replace(/YYYY/g, 'yyyy')  // Full year (2024)
            .replace(/YY/g, 'yy')  // Year (24)
            .replace(/D/g, 'd')  // Day of month (1-31, no leading zero)
            .replace(/M/g, 'M')  // Month (1-12, no leading zero)
            .replace(/Y/g, 'y');  // Year (24, no leading zero)
    }

    /**
     * Gets the datepicker format based on user's preferred date format
     * Convenience method that combines conversion with user preference
     * 
     * @param userDateFormat - User's preferred date format
     * @returns moment.js format string for datepicker
     */
    getDatePickerFormat(userDateFormat: string): string {
        return this.convertToMomentFormat(userDateFormat);
    }
}
